<template>
  <div class="admin-page">
    <!-- ── 页头 ───────────────────────────────────────────── -->
    <div class="page-head">
      <div class="head-text">
        <h1>系统配置</h1>
        <p>全站经济参数、功能开关与第三方接入凭据</p>
      </div>
    </div>

    <!-- 换算说明：管理员必须先知道"库里存元、界面看豆"，否则会以为数值变了 -->
    <div v-if="beanGroup" class="hint-bar config-tip">
      <i class="el-icon-warning-outline" />
      <span>
        爱豆按「1 元 = <b>{{ beanPerYuan }}</b> 豆」换算。<b>本页所有以「豆」为单位的项，填豆数即可</b>，系统会自动换算成金额存储（例如填 20，实际存 2.00 元）。
      </span>
    </div>

    <!-- 按分组分卡片渲染。爱豆放最前 —— 它是这个产品最常改的一组 -->
    <div
      v-for="group in groups"
      :key="group.key"
      class="panel group-card"
      :class="{ 'group-card--bean': group.key === 'bean' }"
    >
      <div class="group-head">
        <div class="group-head__l">
          <span class="group-head__title">{{ group.title }}</span>
          <span class="group-head__desc">{{ group.desc }}</span>
        </div>
        <span v-if="group.key === 'bean'" class="tag is-rose">核心</span>
      </div>

      <div class="rows">
        <div
          v-for="row in group.rows"
          :key="row.configKey"
          class="row"
          :class="{ 'row--dirty': isDirty(row) }"
        >
          <!-- 左：名称 + 配置键（键对管理员没用，但排查问题时要，所以留小字） -->
          <div class="row__label">
            <div class="row__name">{{ row.configDesc }}</div>
            <div class="row__key">{{ row.configKey }}</div>
          </div>

          <!-- 右：按单位渲染不同控件 -->
          <div class="row__control">
            <!-- 开关 -->
            <el-switch
              v-if="row.valueUnit === 'SWITCH'"
              v-model="row.editValue"
              active-value="1"
              inactive-value="0"
              active-text="开启"
              inactive-text="关闭"
            />

            <!-- 爱豆：输入豆数，下方实时显示实际存储的金额 -->
            <div v-else-if="row.valueUnit === 'BEAN'" class="bean-input">
              <el-input-number
                v-model="row.editValue"
                :min="0"
                :max="99999"
                :step="5"
                size="small"
                controls-position="right"
              />
              <span class="unit">豆</span>
              <span class="hint">= {{ toYuan(row.editValue) }} 元</span>
            </div>

            <!-- 换算率：它自己就是"多少豆 / 元"，不参与换算 -->
            <div v-else-if="row.valueUnit === 'RATE'" class="count-input">
              <el-input-number
                v-model="row.editValue"
                :min="1"
                :max="10000"
                :step="10"
                size="small"
                controls-position="right"
              />
              <span class="unit">豆 / 元</span>
              <span class="hint hint--wide">改这里会整体影响全站豆数</span>
            </div>

            <!-- 次数
                 ⛔⛔ 上限**不能**是 99。
                     el-input-number 的 watch 是 immediate 的，初始值一旦 >= max，
                     它会 `$emit('input', max)` 把 v-model 静默改成上限值 ——
                     于是 origin=600、editValue 被夹成 99，isDirty 立刻为 true，
                     管理员再点一次「保存」就把库里的 600 真的写成 99。
                     「次数」类配置里 600（限流）、300（限流）、100（告警）都是合法值，
                     2026-10-19 实测被夹过。上限只是防手滑，不是业务约束，
                     业务上限由后端 validateValue 负责。 -->
            <div v-else-if="row.valueUnit === 'COUNT'" class="count-input">
              <el-input-number
                v-model="row.editValue"
                :min="0"
                :max="unitMax(row)"
                size="small"
                controls-position="right"
              />
              <span class="unit">次</span>
              <span v-if="isOverUnitMax(row)" class="hint hint--over">
                当前值超出控件常用上限 {{ UNIT_MAX[row.valueUnit] }}
              </span>
            </div>

            <!-- 毫秒
                 ⛔ 广告「看完」最短时长（ad.min_complete_ms = 5000）是**毫秒**，
                    以前被标成 COUNT 就变成「5000 次」还被夹到 99，防刷奖阈值等于作废。
                     上限 60000 与后端 BeanConfigService#getAdMinCompleteMs 的
                     Math.min(v, 60_000) 对齐，两边口径一致。 -->
            <div v-else-if="row.valueUnit === 'MILLISECOND'" class="count-input">
              <el-input-number
                v-model="row.editValue"
                :min="0"
                :max="unitMax(row)"
                :step="1000"
                size="small"
                controls-position="right"
              />
              <span class="unit">毫秒</span>
              <span class="hint">= {{ (Number(row.editValue || 0) / 1000).toFixed(1) }} 秒</span>
            </div>

            <!-- 秒 / 分钟
                 ⛔ 时间类的配置**不能**用上面的 COUNT：那个控件上限 99，
                    学生认证 SLA 填 240 会被静默夹成 99，而且一保存就把库改成 99。
                    语义也对不上 —— 没人希望在「超时多少分钟」后面看到「次」。 -->
            <div v-else-if="row.valueUnit === 'SECOND'" class="count-input">
              <el-input-number
                v-model="row.editValue"
                :min="0"
                :max="3600"
                :step="10"
                size="small"
                controls-position="right"
              />
              <span class="unit">秒</span>
            </div>

            <div v-else-if="row.valueUnit === 'MINUTE'" class="count-input">
              <el-input-number
                v-model="row.editValue"
                :min="0"
                :max="1440"
                :step="5"
                size="small"
                controls-position="right"
              />
              <span class="unit">分钟</span>
            </div>

            <!-- 小时（置顶时长这类，别显示成"次"） -->
            <div v-else-if="row.valueUnit === 'HOUR'" class="count-input">
              <el-input-number
                v-model="row.editValue"
                :min="1"
                :max="720"
                size="small"
                controls-position="right"
              />
              <span class="unit">小时</span>
            </div>

            <!-- 密钥 -->
            <el-input
              v-else-if="row.valueUnit === 'SECRET'"
              v-model="row.editValue"
              size="small"
              type="password"
              show-password
              class="text-input"
            />

            <!-- 图片（如关于页 logo）：左预览、右上传，地址仍可手填 -->
            <div v-else-if="row.valueUnit === 'IMAGE'" class="image-input">
              <div class="image-box" :class="{ 'image-box--empty': !row.editValue }">
                <img v-if="row.editValue" :src="row.editValue" alt="">
                <i v-else class="el-icon-picture-outline" />
              </div>
              <el-input
                v-model="row.editValue"
                size="small"
                class="text-input text-input--image"
                placeholder="留空则用内置图形"
              />
              <button
                type="button"
                class="act"
                :disabled="uploadingKey === row.configKey"
                @click="pickImage(row)"
              >{{ uploadingKey === row.configKey ? '上传中' : '上传' }}</button>
            </div>

            <!-- 普通文本 -->
            <el-input
              v-else
              v-model="row.editValue"
              size="small"
              class="text-input"
            />

            <button
              type="button"
              class="act save-act"
              :class="isDirty(row) ? 'act-primary' : 'act-disabled'"
              :disabled="!isDirty(row)"
              @click="save(row)"
            >保存</button>
          </div>
        </div>
      </div>
    </div>

    <!-- 新增配置：低频操作，收在底部，不跟日常调参抢位置 -->
    <div class="panel group-card">
      <div class="group-head">
        <div class="group-head__l">
          <span class="group-head__title">新增配置</span>
          <span class="group-head__desc">仅在需要接入新参数时使用</span>
        </div>
        <el-button size="small" class="ghost-btn" @click="handleCreate">新增</el-button>
      </div>
    </div>

    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="520px">
      <el-form ref="form" :model="form" :rules="rules" label-width="100px">
        <el-form-item label="配置键" prop="configKey">
          <el-input v-model="form.configKey" placeholder="如 sign_in.reward，建议用「分组.名称」" />
        </el-form-item>
        <el-form-item label="配置描述" prop="configDesc">
          <el-input v-model="form.configDesc" placeholder="写给管理员看的一句话说明" />
        </el-form-item>
        <el-form-item label="分组" prop="configGroup">
          <el-select v-model="form.configGroup" placeholder="请选择分组" style="width: 100%;">
            <el-option v-for="g in groupOptions" :key="g.key" :label="g.title" :value="g.key" />
          </el-select>
        </el-form-item>
        <el-form-item label="值单位" prop="valueUnit">
          <el-select v-model="form.valueUnit" placeholder="请选择值单位" style="width: 100%;">
            <el-option label="爱豆（按 10:1 换算存储）" value="BEAN" />
            <el-option label="次数" value="COUNT" />
            <el-option label="毫秒" value="MILLISECOND" />
            <el-option label="秒" value="SECOND" />
            <el-option label="分钟" value="MINUTE" />
            <el-option label="小时" value="HOUR" />
            <el-option label="换算率（豆/元）" value="RATE" />
            <el-option label="开关" value="SWITCH" />
            <el-option label="密钥（界面隐藏）" value="SECRET" />
            <el-option label="图片（可上传）" value="IMAGE" />
            <el-option label="文本" value="TEXT" />
          </el-select>
        </el-form-item>
        <el-form-item label="配置值" prop="configValue">
          <el-input v-model="form.configValue" :placeholder="form.valueUnit === 'BEAN' ? '填豆数，例如 20' : '请输入配置值'" />
          <div v-if="form.valueUnit === 'BEAN'" class="form-hint">将按 {{ beanPerYuan }} 豆 = 1 元存储</div>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button class="ghost-btn" @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" class="primary-btn" @click="handleSave">确定</el-button>
      </div>
    </el-dialog>

    <!-- IMAGE 类型配置项的上传入口：一个共享的隐藏 file input，点哪一行就写哪一行 -->
    <input ref="imageInput" type="file" accept="image/*" class="hidden-input" @change="onImageChange">
  </div>
</template>

<script>
import { getConfigList, createConfig, updateConfig } from '@/api/config'
import { uploadImage } from '@/api/upload'

/** 兜底换算率：仅在后端配置读不到时使用，与后端 ConfigKeys.DEFAULT_BEAN_PER_YUAN 一致 */
const FALLBACK_BEAN_PER_YUAN = 10

/** 上传图片大小上限（logo 这类用不到更大的图） */
const MAX_IMAGE_SIZE = 5 * 1024 * 1024

/**
 * 各单位在界面上允许输入的上限。
 * ⛔ 这只是**防手滑**，不是业务约束 —— 真正的合法性由后端 AdminConfigController#validateValue 判断。
 * ⛔⛔ 定小了会出事故：el-input-number 初始值 >= max 时会静默把值改成 max 并回写 v-model，
 *      管理员一进来那行就变成"已修改"，点保存就把库改小（2026-10-19 踩过，COUNT 定的 99）。
 *      所以这里一律给足余量，且 unitMax() 还会兜住"库里值本身超过上限"的情况。
 */
const UNIT_MAX = {
  COUNT: 999999,
  MILLISECOND: 60000,
  SECOND: 3600,
  MINUTE: 1440,
  HOUR: 720,
  RATE: 10000,
  BEAN: 999999
}

/** 走数字控件的单位（同时用于 toEdit / toStore / configType 判定，别在四处各写一遍） */
const NUMERIC_UNITS = ['COUNT', 'MILLISECOND', 'SECOND', 'MINUTE', 'HOUR', 'RATE', 'BEAN']

const GROUP_META = [
  { key: 'bean', title: '爱豆', desc: '投放纸条、取出纸条消耗的豆，以及签到、邀请、新手赠送的来源' },
  { key: 'ad', title: '激励视频', desc: '免费次数用完后，看广告再换机会。次数填 0 即关闭入口；广告位 ID 留空则该侧不可用' },
  { key: 'school', title: '学校开通', desc: '试点学校在用户端的标签文案，以及学生可修改学校的间隔' },
  { key: 'moment', title: '动态', desc: '动态的审核口径（是否全部先审后发）与付费置顶的定价、时长，改完即时生效' },
  { key: 'comment', title: '评论', desc: '评论与回复是否全部先审后发。打开后全站评论都要人工审核通过才展示，互动会明显变慢，没有内容压力时建议关闭' },
  { key: 'verify', title: '学生认证', desc: '认证通过后的额外激励（可选功能，只会多得不会少得）' },
  { key: 'file', title: '文件存储', desc: '七牛云存储参数与存储方式开关（密钥类项界面隐藏，改完即生效）' },
  { key: 'notice', title: '通知', desc: '待办推到钉钉群的开关、免打扰时段、催办时长与合并窗口。群本身在「通知群配置」里维护' },
  { key: 'system', title: '系统信息', desc: '应用名称、logo、版本号、说明与联系方式 —— 小程序「关于」页与管理端登录页、侧边栏共用同一份' },
  { key: 'wechat', title: '微信小程序', desc: '小程序 AppID 与密钥，修改后即时生效' },
  { key: 'pay', title: '微信支付', desc: '商户号与证书，修改后即时生效' }
]

export default {
  name: 'Config',
  data() {
    return {
      configList: [],
      beanPerYuan: FALLBACK_BEAN_PER_YUAN,
      /** 模板里要读，所以放到 data（模块级常量不会被模板代理） */
      UNIT_MAX,
      dialogVisible: false,
      dialogTitle: '新增配置',
      /** 正在上传的配置项 key（用于只让被点的那一行转圈） */
      uploadingKey: '',
      /** 上传完成后要写回的行 */
      uploadRow: null,
      form: {
        configKey: '',
        configDesc: '',
        configGroup: 'bean',
        valueUnit: 'TEXT',
        configValue: ''
      },
      rules: {
        configKey: [{ required: true, message: '请输入配置键', trigger: 'blur' }],
        configDesc: [{ required: true, message: '请输入配置描述', trigger: 'blur' }],
        configGroup: [{ required: true, message: '请选择分组', trigger: 'change' }],
        valueUnit: [{ required: true, message: '请选择值单位', trigger: 'change' }],
        configValue: [{ required: true, message: '请输入配置值', trigger: 'blur' }]
      }
    }
  },
  computed: {
    groupOptions() {
      return GROUP_META.map(g => ({ key: g.key, title: g.title }))
    },
    /** 按分组归拢，未归类的统一落到「系统信息」里，避免有配置在界面上"消失" */
    groups() {
      const map = {}
      this.configList.forEach(item => {
        const key = GROUP_META.some(g => g.key === item.configGroup) ? item.configGroup : 'system'
        if (!map[key]) map[key] = []
        map[key].push(item)
      })
      return GROUP_META
        .filter(g => map[g.key] && map[g.key].length)
        .map(g => ({ key: g.key, title: g.title, desc: g.desc, rows: map[g.key] }))
    },
    beanGroup() {
      return this.groups.some(g => g.key === 'bean')
    }
  },
  created() {
    this.fetchData()
  },
  methods: {
    fetchData() {
      getConfigList().then(res => {
        const list = (res.data && res.data.configs) || []
        // 换算率本身也是配置项，所以要先把它读出来，才能正确地把其他项换算成豆
        const rateRow = list.find(i => i.configKey === 'bean.per_yuan')
        const rate = rateRow ? Number(rateRow.configValue) : 0
        this.beanPerYuan = rate > 0 ? rate : FALLBACK_BEAN_PER_YUAN

        // editValue 是编辑缓冲：BEAN 单位换成人看得懂的豆数，其余原样
        this.configList = list.map(item => ({
          ...item,
          editValue: this.toEdit(item),
          origin: this.toEdit(item)
        }))
      })
    },
    /** 库中的值 → 界面上的值 */
    toEdit(item) {
      const raw = item.configValue
      if (item.valueUnit === 'BEAN') {
        const yuan = parseFloat(raw)
        return isNaN(yuan) ? 0 : Math.round(yuan * this.beanPerYuan)
      }
      if (NUMERIC_UNITS.indexOf(item.valueUnit) >= 0) {
        const n = parseInt(raw, 10)
        return isNaN(n) ? 0 : n
      }
      return raw
    },
    /** 界面上的值 → 库中的值 */
    toStore(row) {
      if (row.valueUnit === 'BEAN') {
        return (Number(row.editValue || 0) / this.beanPerYuan).toFixed(2)
      }
      if (NUMERIC_UNITS.indexOf(row.valueUnit) >= 0) {
        return String(Number(row.editValue || 0))
      }
      return row.editValue
    },
    toYuan(bean) {
      return (Number(bean || 0) / this.beanPerYuan).toFixed(2)
    },
    /**
     * 控件上限。
     * ⛔⛔ 必须取 max(该单位常用上限, 当前值) —— 不能直接返回常量：
     *    el-input-number 在初始值 >= max 时会把值改成 max 并回写 v-model，
     *    库里本来是 600，页面一打开就变 99，再点保存就真的写进库了。
     *    取 max 之后，超限的历史值不会被改写，界面上另外用 isOverUnitMax 标出来。
     */
    unitMax(row) {
      const base = UNIT_MAX[row.valueUnit] || 999999
      const cur = Number(row.origin) || 0
      return Math.max(base, cur)
    },
    /** 库里的值本身超出了该单位的常用上限 —— 不拦着改，但要让管理员看见 */
    isOverUnitMax(row) {
      const base = UNIT_MAX[row.valueUnit]
      if (!base) return false
      return (Number(row.origin) || 0) > base
    },
    isDirty(row) {
      return String(row.editValue) !== String(row.origin)
    },
    save(row) {
      if (row.valueUnit === 'SWITCH') {
        // 开关直接提交，不需要二次确认
        this.commit(row)
        return
      }
      if (row.valueUnit === 'SECRET') {
        this.$confirm('密钥保存后立即生效，填错会立刻影响对应功能，确定保存吗？', '提示', { type: 'warning' })
          .then(() => this.commit(row))
          .catch(() => {})
        return
      }
      const tip = row.valueUnit === 'BEAN'
        ? `「${row.configDesc}」将由 ${row.origin} 豆改为 ${row.editValue} 豆（存储为 ${this.toYuan(row.editValue)} 元），确定吗？`
        : `「${row.configDesc}」将由 ${row.origin} 改为 ${row.editValue}，确定吗？`
      this.$confirm(tip, '确认修改', { type: 'warning' })
        .then(() => this.commit(row))
        .catch(() => {})
    },
    commit(row) {
      const data = {
        configValue: this.toStore(row),
        configType: row.configType,
        description: row.configDesc,
        isPublic: row.isPublic,
        configGroup: row.configGroup,
        valueUnit: row.valueUnit
      }
      updateConfig(row.configKey, data).then(() => {
        this.$message.success('已保存')
        row.origin = row.editValue
      }).catch(() => {
        this.$message.error('保存失败')
      })
    },
    /** 点击某行的「上传」：记下这一行，再交给共享的 file input */
    pickImage(row) {
      this.uploadRow = row
      this.$refs.imageInput.click()
    },
    onImageChange(e) {
      const file = e.target.files && e.target.files[0]
      // 先清空 value：同一个文件连选两次也要能触发 change
      e.target.value = ''
      const row = this.uploadRow
      if (!file || !row) return
      if (file.type.indexOf('image/') !== 0) {
        this.$message.error('请选择图片文件')
        return
      }
      if (file.size > MAX_IMAGE_SIZE) {
        this.$message.error('图片不要超过 5MB')
        return
      }
      this.uploadingKey = row.configKey
      uploadImage(file, 'SYSTEM_LOGO').then(res => {
        this.uploadingKey = ''
        row.editValue = (res.data && res.data.url) || ''
        // 刻意不自动提交：这一页所有改动都是「改完点保存」，图片不该是例外，
        // 否则运营点完上传就走了，会以为存上了
        this.$message.success('上传成功，点「保存」后生效')
      }).catch(() => {
        this.uploadingKey = ''
      })
    },
    handleCreate() {
      this.dialogTitle = '新增配置'
      this.form = {
        configKey: '',
        configDesc: '',
        configGroup: 'bean',
        valueUnit: 'TEXT',
        configValue: ''
      }
      this.dialogVisible = true
    },
    handleSave() {
      this.$refs.form.validate(valid => {
        if (!valid) return
        const payload = { ...this.form }
        if (payload.valueUnit === 'BEAN') {
          payload.configValue = (Number(payload.configValue || 0) / this.beanPerYuan).toFixed(2)
        }
        // ⛔ 用 NUMERIC_UNITS 统一判定，别在这里再抄一份单位清单 ——
        //    上一版漏了 MILLISECOND，新增的毫秒配置会被存成 STRING 类型
        payload.configType = payload.valueUnit === 'SWITCH' ? 'SWITCH'
          : NUMERIC_UNITS.indexOf(payload.valueUnit) >= 0 ? 'NUMBER'
          : 'STRING'
        createConfig(payload).then(() => {
          this.$message.success('新增成功')
          this.dialogVisible = false
          this.fetchData()
        }).catch(() => {
          this.$message.error('新增失败')
        })
      })
    }
  }
}
</script>

<style scoped>
.config-tip {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 16px;
  line-height: 1.7;
}

.config-tip i {
  margin-top: 2px;
  flex: 0 0 auto;
}

.group-card {
  padding: 0 18px;
  margin-bottom: 16px;
}

.group-card--bean {
  border-top: 3px solid var(--rose-400);
}

.group-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 0 14px;
  border-bottom: 1px solid var(--ink-100);
}

.group-head__l {
  display: flex;
  align-items: baseline;
  gap: 12px;
  min-width: 0;
}

.group-head__title {
  font-size: 15px;
  font-weight: 600;
  color: var(--ink-900);
}

.group-head__desc {
  font-size: 12px;
  color: var(--ink-400);
}

.rows {
  margin: -4px 0;
}

.row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 14px 4px;
  border-bottom: 1px solid var(--ink-100);
}

.row:last-child {
  border-bottom: none;
}

/* 有未保存改动时左侧亮一条玫红，一眼看到"哪一项动过还没存" */
.row--dirty {
  box-shadow: inset 3px 0 0 var(--rose-400);
  background-color: #fffafb;
}

.row__label {
  flex: 1;
  min-width: 0;
}

.row__name {
  font-size: 13.5px;
  color: var(--ink-900);
  line-height: 1.5;
}

.row__key {
  font-size: 11.5px;
  color: var(--ink-350);
  font-family: 'SFMono-Regular', Consolas, monospace;
  margin-top: 2px;
}

.row__control {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.bean-input,
.count-input {
  display: flex;
  align-items: center;
}

.unit {
  font-size: 13px;
  color: var(--ink-500);
  margin-left: 8px;
}

.hint {
  font-size: 12px;
  color: var(--ink-350);
  margin-left: 12px;
  min-width: 74px;
}

.hint--wide {
  min-width: 150px;
}

/* 库里值本身超出控件常用上限时的提示：用警示色，但不做成按钮（它不是动作） */
.hint--over {
  color: var(--coral-600, #b4441f);
  font-weight: 500;
}

.text-input {
  width: 320px;
}

/* ===== 图片类配置（关于页 logo）===== */
.image-input {
  display: flex;
  align-items: center;
  gap: 10px;
}

.image-box {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  overflow: hidden;
  flex-shrink: 0;
  background-color: var(--paper-100, #f4ece6);
  border: 1px solid var(--ink-100, #e8e0e6);
  display: flex;
  align-items: center;
  justify-content: center;
}

.image-box img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.image-box--empty i {
  font-size: 18px;
  color: var(--ink-300, #a79ba9);
}

.text-input--image {
  width: 210px;
}

.hidden-input {
  display: none;
}

.form-hint {
  font-size: 12px;
  color: var(--ink-400);
  margin-top: 4px;
}

/* 保存按钮：无改动时保持灰，有改动才点亮 */
.save-act {
  flex: 0 0 auto;
  min-width: 62px;
}

.act-disabled {
  color: var(--ink-300);
  border-color: var(--ink-200);
  cursor: not-allowed;
}
</style>
